import type { TFunction } from "i18next";
import { useCallback, useEffect, useRef } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000AutoCompleteInput from "../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import type { U3139DetailLotsHandler } from "./U3139DetailLotsHandlers";
import { useU3139FindCadastreHandlers } from "../../../../U3139UseFindCadastreHandlers";
import { useU3139FindDistrictHandlers } from "../../../../U3139UseFindDistrictHandlers";
import {
  u3139ExtractDetailLotCadastreFields,
  u3139ExtractDetailLotDistrictFields,
  u3139FormatCadastreRankDisplay,
  u3139FormatDistrictLabel,
  u3139FormatFindCadastreLabel,
  type U3139DetailLot,
  type U3139FindCadastreItem,
} from "../../../../U3139Service";

interface U3139DetailLotsEditDlgProps {
  handler: U3139DetailLotsHandler;
  t: TFunction;
  NS: string;
}

function U3139DetailLotsEditDlg({ handler, t, NS }: U3139DetailLotsEditDlgProps) {
  const { formStateHandlers } = handler;
  const findDistrictHandlers = useU3139FindDistrictHandlers();
  const districtId = formStateHandlers.getData()?.district?.id?.trim();
  const findCadastreHandlers = useU3139FindCadastreHandlers({
    getDistrictId: () => districtId,
  });
  const setCadastreSelection = useRef<((item: unknown) => void) | null>(null);
  const setCadastreInputValue = useRef<((value: string) => void) | null>(null);

  const onSearchDistrict = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => findDistrictHandlers.search(criterias),
    [findDistrictHandlers],
  );

  const onSearchCadastre = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      const result = await findCadastreHandlers.search(criterias);
      return {
        ...result,
        results: result.results.map((item) => ({
          ...item,
          rankLabel: u3139FormatCadastreRankDisplay(item.rankCode, item.rankLabel),
        })),
      };
    },
    [findCadastreHandlers],
  );

  const extractDistrict = useCallback(
    (result: Record<string, unknown> | null) => u3139ExtractDetailLotDistrictFields(result),
    [],
  );

  const extractCadastre = useCallback(
    (result: Record<string, unknown> | null) => u3139ExtractDetailLotCadastreFields(result),
    [],
  );

  const displayDistrict = useCallback(
    (item: { propertyDistrict?: string } | null) => u3139FormatDistrictLabel(item),
    [],
  );

  const displayCadastre = useCallback(
    (item: U3139FindCadastreItem) => u3139FormatFindCadastreLabel(item),
    [],
  );

  useEffect(() => {
    if (districtId) return;
    setCadastreSelection.current?.(null);
    setCadastreInputValue.current?.("");
    const current = formStateHandlers.getData();
    if (current?.cadastre) {
      formStateHandlers.setData({ ...current, cadastre: null } as U3139DetailLot);
    }
  }, [districtId, formStateHandlers]);

  return (
    <U2000SaveCancelDialog
      isOpen={handler.isDialogOpen}
      onClose={handler.onDlgClose}
      title={t("lblDetailLot", { ns: NS })}
      isValid
    >
      <div className="flex flex-col gap-y-4 min-w-md">
        <U2000InputText fieldName="initialLot" label={t("lblInitialLot", { ns: NS })} formStateHandlers={formStateHandlers} />
        <U2000AutoCompleteInput
          fieldName="district"
          mainFieldName="district.propertyDistrict"
          label={t("lblDistrict", { ns: NS })}
          formStateHandlers={formStateHandlers}
          onSearch={onSearchDistrict}
          overrideExtractDataFn={extractDistrict}
          displayFn={displayDistrict}
          pageSize={15}
          basicResultHeight={300}
          minCarLaunchSearch={0}
          resultLabels={[]}
          resultFields={["propertyDistrict"]}
        />
        <U2000AutoCompleteInput
          fieldName="cadastre"
          mainFieldName="cadastre.cadastre"
          label={t("lblCadastreOptional", { ns: NS })}
          formStateHandlers={formStateHandlers}
          onSearch={onSearchCadastre}
          overrideExtractDataFn={extractCadastre}
          displayFn={(item) => displayCadastre(item as U3139FindCadastreItem)}
          onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
            setCadastreSelection.current = setCurrentSelection as (item: unknown) => void;
            setCadastreInputValue.current = setInputValue;
          }}
          pageSize={15}
          basicResultHeight={300}
          minCarLaunchSearch={0}
          resultLabels={[]}
          resultFields={["cadastre", "rankLabel"]}
        />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3139DetailLotsEditDlg;
